import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Send } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Chat panel for an event. Polls every 5s.
 *
 * Props:
 *   eventId: string
 *   apiClient: axios instance (api for coordinators, photoApi for photographers)
 *   selfName: string used for "You" label match
 */
export default function EventChat({ eventId, apiClient, selfName }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const lastTsRef = useRef(null);
  const scrollerRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const params = lastTsRef.current ? { since: lastTsRef.current } : {};
      const r = await apiClient.get(`/events/${eventId}/chat`, { params });
      const newMsgs = r.data || [];
      if (newMsgs.length) {
        setMessages((prev) => [...prev, ...newMsgs]);
        lastTsRef.current = newMsgs[newMsgs.length - 1].created_at;
      }
    } catch (e) {
      // swallow during polling; first load surfaces toast
      if (!lastTsRef.current) toast.error(t('chat.loadError'));
    }
  };

  useEffect(() => {
    fetchMessages();
    const id = setInterval(fetchMessages, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [eventId]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages]);

  const onSend = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const r = await apiClient.post(`/events/${eventId}/chat`, { text: trimmed });
      setMessages((prev) => [...prev, r.data]);
      lastTsRef.current = r.data.created_at;
      setText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('chat.loadError'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden h-[calc(100vh-220px)] min-h-[480px]">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
        <h3 className="font-semibold text-slate-800">{t('chat.title')}</h3>
      </div>
      <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50/40">
        {messages.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">{t('chat.empty')}</p>
        ) : (
          messages.map((m) => {
            const isSelf = (m.sender_name === selfName) ||
              (m.sender_type === 'coordinator' && selfName === 'coordinator');
            return (
              <div key={m.id} className={`flex ${isSelf ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${
                  isSelf ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200 text-slate-800'
                }`}>
                  {!isSelf && (
                    <p className="text-xs font-semibold opacity-80 mb-0.5">
                      {m.sender_name}
                      {m.sender_type === 'coordinator' && (
                        <span className="ml-1 text-[10px] uppercase tracking-wide opacity-60">
                          · {t('chat.coordinator')}
                        </span>
                      )}
                    </p>
                  )}
                  <p className="whitespace-pre-wrap break-words">{m.text}</p>
                  <p className={`text-[10px] mt-1 ${isSelf ? 'text-emerald-50' : 'text-slate-400'}`}>
                    {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={onSend} className="flex items-center gap-2 p-3 border-t border-slate-200">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('chat.placeholder')}
          maxLength={4000}
          className="flex-1 h-10 px-3 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm"
        />
        <Button type="submit" disabled={sending || !text.trim()}>
          <Send className="w-4 h-4" />
          <span className="ml-1 hidden sm:inline">{t('chat.send')}</span>
        </Button>
      </form>
    </div>
  );
}
